<template>
  <div>
    <div v-if="loading" class="text-center py-6"><span class="spinner-border text-primary"></span></div>

    <template v-else>
      <div class="d-flex flex-wrap gap-2 mb-4">
        <span v-for="(count, status) in counts" :key="status" class="badge" :class="checklistStatus(status).badge">
          {{ checklistStatus(status).label }}: {{ count }}
        </span>
      </div>

      <div v-for="group in groups" :key="group.period" class="mb-4">
        <div class="fw-bolder fs-7 text-uppercase text-muted mb-2">{{ group.period }}</div>
        <div class="d-flex flex-wrap gap-2">
          <button v-for="item in group.items" :key="item.key" type="button"
                  class="btn btn-sm btn-outline btn-outline-dashed py-1 px-3 text-start prenatal-check-item"
                  :title="item.condition || ''"
                  :disabled="readonly || item.status === 'done' || item.status === 'not_applicable'"
                  @click="startCreate(item.exam_code)">
            <span class="badge me-2" :class="checklistStatus(item.status).badge">{{ checklistStatus(item.status).label }}</span>
            <span class="fs-7">{{ item.label }}</span>
            <span v-if="item.condition" class="text-muted fs-8 ms-1">({{ item.condition.toLowerCase() }})</span>
            <span v-if="item.exam_date" class="text-muted fs-8 ms-1">· {{ formatDateBr(item.exam_date) }}</span>
          </button>
        </div>
      </div>

      <div class="separator separator-dashed my-4"></div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="fw-bolder fs-6">Resultados registrados</div>
        <button v-if="!readonly && !form" type="button" class="btn btn-sm btn-light-primary" @click="startCreate()">
          <i class="bi bi-plus-lg me-1"></i> Exame
        </button>
      </div>

      <div v-if="form" class="card card-bordered mb-4">
        <div class="card-body p-4">
          <div class="row g-3">
            <div class="col-md-5">
              <label class="form-label fs-7">Exame</label>
              <select class="form-select form-select-sm" :class="{ 'is-invalid': errors.exam_code }" v-model="form.exam_code">
                <option v-for="option in examOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <small class="text-danger" v-if="errors.exam_code">{{ errors.exam_code }}</small>
            </div>
            <div class="col-md-4" v-if="form.exam_code === 'other'">
              <label class="form-label fs-7">Nome do exame</label>
              <input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': errors.exam_name }" v-model="form.exam_name">
              <small class="text-danger" v-if="errors.exam_name">{{ errors.exam_name }}</small>
            </div>
            <div class="col-md-3">
              <label class="form-label fs-7">Data da coleta</label>
              <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.exam_date }" v-model="form.exam_date" :max="today">
              <small class="text-danger" v-if="errors.exam_date">{{ errors.exam_date }}</small>
            </div>
            <div class="col-md-6">
              <label class="form-label fs-7">Resultado</label>
              <input type="text" class="form-control form-control-sm" :class="{ 'is-invalid': errors.result }" v-model="form.result"
                     :placeholder="form.exam_code === 'blood_type' ? 'Ex.: O negativo' : ''">
              <small class="text-danger" v-if="errors.result">{{ errors.result }}</small>
            </div>
            <div class="col-md-2">
              <label class="form-label fs-7">Valor</label>
              <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors.value }" v-model="form.value">
              <small class="text-danger" v-if="errors.value">{{ errors.value }}</small>
            </div>
            <div class="col-md-2">
              <label class="form-label fs-7">Unidade</label>
              <input type="text" class="form-control form-control-sm" v-model="form.unit">
            </div>
            <div class="col-md-2">
              <label class="form-label fs-7">Interpretação</label>
              <select class="form-select form-select-sm" v-model="form.interpretation">
                <option v-for="option in interpretationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fs-7">Observações</label>
              <input type="text" class="form-control form-control-sm" v-model="form.notes">
            </div>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-sm btn-light" @click="form = null">Cancelar</button>
            <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span> Salvar
            </button>
          </div>
        </div>
      </div>

      <div v-if="!exams.length" class="text-muted fs-7 text-center py-4">Nenhum exame registrado.</div>
      <div v-else class="table-responsive">
        <table class="table table-sm table-row-dashed align-middle fs-7">
          <thead>
          <tr class="text-muted fs-8 text-uppercase">
            <th>Data</th>
            <th>IG</th>
            <th>Exame</th>
            <th>Resultado</th>
            <th>Interpretação</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="exam in exams" :key="exam.id">
            <td>{{ formatDateBr(exam.exam_date) }}</td>
            <td>{{ exam.gestational_age?.label }}</td>
            <td class="fw-bold">{{ exam.label }}</td>
            <td>
              {{ exam.result || '' }}
              <span v-if="exam.value !== null">{{ formatNumber(exam.value, exam.unit, 3) }}</span>
            </td>
            <td>
              <span class="badge" :class="interpretationBadge(exam.interpretation)">{{ exam.interpretation_label }}</span>
            </td>
            <td class="text-end text-nowrap">
              <button v-if="!readonly" type="button" class="btn btn-sm btn-icon btn-light" title="Editar" @click="startEdit(exam)">
                <i class="bi bi-pencil"></i>
              </button>
              <button v-if="!readonly && canDelete" type="button" class="btn btn-sm btn-icon btn-light-danger ms-1" title="Excluir" @click="remove(exam)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import http from '../../../../helpers/http';
import Alert from '../../../../helpers/alert';
import {
  EXAM_INTERPRETATION_OPTIONS,
  EXAM_OPTIONS,
  buildPayload,
  checklistCounts,
  checklistStatus,
  formatDateBr,
  formatNumber,
  groupChecklistByPeriod,
  todayIso,
  validationErrors,
} from '../prenatal';

export default {
  name: 'PrenatalExamsTab',
  emits: ['changed'],
  props: {
    baseUrl: { type: String, required: true },
    readonly: Boolean,
    canDelete: Boolean,
  },
  data() {
    return {
      loading: false,
      saving: false,
      exams: [],
      checklist: [],
      form: null,
      editingId: null,
      errors: {},
      today: todayIso(),
      examOptions: EXAM_OPTIONS,
      interpretationOptions: EXAM_INTERPRETATION_OPTIONS,
    };
  },
  computed: {
    groups() {
      return groupChecklistByPeriod(this.checklist);
    },
    counts() {
      return checklistCounts(this.checklist);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    checklistStatus,
    formatDateBr,
    formatNumber,
    interpretationBadge(interpretation) {
      return {
        normal: 'badge-light-success',
        altered: 'badge-light-danger',
        pending: 'badge-light-warning',
      }[interpretation] ?? 'badge-light';
    },
    async load() {
      this.loading = true;
      try {
        const response = await http.get(`${this.baseUrl}/exams`);
        this.exams = response.data.exams;
        this.checklist = response.data.checklist;
      } catch (_) {
        Alert.toastError('Falha ao carregar os exames.');
      } finally {
        this.loading = false;
      }
    },
    startCreate(examCode = 'hemogram') {
      this.editingId = null;
      this.errors = {};
      this.form = {
        exam_code: examCode,
        exam_name: '',
        exam_date: this.today,
        result: '',
        value: '',
        unit: '',
        interpretation: 'pending',
        notes: '',
      };
    },
    startEdit(exam) {
      this.editingId = exam.id;
      this.errors = {};
      this.form = {
        exam_code: exam.exam_code,
        exam_name: exam.exam_name ?? '',
        exam_date: exam.exam_date,
        result: exam.result ?? '',
        value: exam.value ?? '',
        unit: exam.unit ?? '',
        interpretation: exam.interpretation,
        notes: exam.notes ?? '',
      };
    },
    async save() {
      this.saving = true;
      this.errors = {};
      const payload = buildPayload(this.form, ['value']);

      try {
        if (this.editingId) {
          await http.put(`${this.baseUrl}/exams/${this.editingId}`, payload);
        } else {
          await http.post(`${this.baseUrl}/exams`, payload);
        }
        this.form = null;
        Alert.toastSuccess('Exame salvo.');
        await this.load();
        this.$emit('changed');
      } catch (error) {
        this.errors = validationErrors(error);
        if (!Object.keys(this.errors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao salvar o exame.');
        }
      } finally {
        this.saving = false;
      }
    },
    async remove(exam) {
      const result = await Swal.fire({
        title: 'Excluir exame?',
        text: `${exam.label} de ${formatDateBr(exam.exam_date)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' },
      });
      if (!result.isConfirmed) {
        return;
      }

      try {
        await http.delete(`${this.baseUrl}/exams/${exam.id}`);
        await this.load();
        this.$emit('changed');
      } catch (error) {
        Alert.toastError(error.response?.data?.message || 'Falha ao excluir o exame.');
      }
    },
  },
};
</script>

<style scoped>
.prenatal-check-item:disabled {
  opacity: 1;
}
</style>
